import { useEffect } from 'react';
import {
  SITE, canonicalFor, metaForPath, pageTitle, siteJsonLd, type PageMeta,
} from './seo';

/**
 * Writes a page's metadata into the document head.
 *
 * ── Why this is imperative rather than a component ────────────────────────
 *
 * There is exactly one `<head>`, and two components each rendering their own
 * `<title>` would produce two. Managing it as a side effect keyed on the route
 * means the last route to mount wins, which is the behaviour anybody expects,
 * and it needs no library.
 *
 * Every tag is *replaced* rather than appended, and everything this module
 * writes carries `data-seo`, so a navigation cannot leave the previous page's
 * description sitting underneath the new one — which is the failure mode of
 * every hand-rolled version of this, and invisible until somebody shares a
 * link and gets the wrong preview.
 *
 * The prerenderer (`scripts/prerender.mjs`) captures the head this produces,
 * so what a crawler reads without running any JavaScript is exactly what this
 * writes.
 */
export function useSeo(meta: PageMeta | null, extraJsonLd?: Record<string, unknown>[]) {
  useEffect(() => {
    if (!meta) return undefined;

    document.title = pageTitle(meta);
    document.documentElement.setAttribute('lang', SITE.locale);

    const canonical = canonicalFor(meta.path);
    const image = `${SITE.url}${SITE.ogImage}`;

    setMeta('name', 'description', meta.description);
    /*
     * Written on every page, including the ones that should be indexed.
     *
     * An absent robots tag means "index" — so only writing it on noindex
     * pages would work, right up until a navigation from a noindex page to an
     * indexable one left the old tag in place. Writing both values every time
     * removes the ordering question entirely.
     */
    setMeta('name', 'robots', meta.noindex ? 'noindex, nofollow' : 'index, follow');

    setLink('canonical', canonical);

    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', SITE.name);
    setMeta('property', 'og:title', pageTitle(meta));
    setMeta('property', 'og:description', meta.description);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', image);
    setMeta('property', 'og:locale', SITE.locale.replace('-', '_'));

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', pageTitle(meta));
    setMeta('name', 'twitter:description', meta.description);
    setMeta('name', 'twitter:image', image);

    setJsonLd([...(meta.jsonLd ?? []), ...(extraJsonLd ?? [])]);

    return undefined;
  }, [meta, extraJsonLd]);
}

/**
 * The metadata for whatever route is showing.
 *
 * A path with no entry in PUBLIC_ROUTES is, by definition, not a public page —
 * the manager app, the tenant portal, the operator console, a mistyped URL —
 * so it gets a noindex rather than nothing. Signed-in screens appearing in
 * search results is the failure this prevents, and "nothing was declared" is
 * not a reason to let them.
 */
export function useRouteSeo(
  pathname: string,
  extraJsonLd?: Record<string, unknown>[],
) {
  const known = metaForPath(pathname);
  const meta: PageMeta = known ?? {
    path: pathname,
    title: SITE.name,
    description: '',
    noindex: true,
  };
  useSeo(meta, known ? extraJsonLd : undefined);
}

export { siteJsonLd };

// ── Head plumbing ───────────────────────────────────────────────────────────

function setMeta(keyName: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${keyName}="${key}"]`;
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(keyName, key);
    tag.setAttribute('data-seo', '');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let tag = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    tag.setAttribute('data-seo', '');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

/**
 * Replaces every block wholesale.
 *
 * Structured data is the one place where leaving a stale tag behind does real
 * damage: two conflicting Offers on one page is a reason for a search engine
 * to stop trusting all of it, not just the wrong one.
 */
function setJsonLd(blocks: Record<string, unknown>[]) {
  document.head
    .querySelectorAll('script[type="application/ld+json"][data-seo]')
    .forEach((node) => node.remove());

  for (const block of blocks) {
    const script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('data-seo', '');
    script.textContent = JSON.stringify(block);
    document.head.appendChild(script);
  }
}
